// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse } from "react-bootstrap";

import { isSet } from "shared/utils/utils";
import { ICloudParameter, IVirtAssign } from "shared/types/cloudtypes";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import {
  makeSortHeader,
  tabClickHandler,
  getVirtualOcppChargerSourceName,
  renderVirtualSiteIcon,
} from "guiutils";
import { IState } from "states/states";
import { IOcppCharger } from "states/ocppstates";
import { ITableSort } from "states/guistates";
import { Site } from "states/sitestates";

import IconLocked from "icons/locked";
import IconUnlocked from "icons/unlocked";
import IconEdit from "icons/iconEdit";
import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

import ModalChargerSettings from "view/modal/ModalChargerSettings";

// sort key of table
const TABLE_NAME = "t_ocpp_chargers";

interface IChargersProps {
  ocppChargers: IOcppCharger[];
  canModifyChargers: boolean;
  showId: boolean;
  tableSort: ITableSort | undefined;
  virtualOcppChargers: IVirtAssign[];
  siteId: number | undefined;
  siteList: Site[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId);
  const props: IChargersProps = {
    ocppChargers: state.ocpp.charger,
    canModifyChargers:
      (curUser?.accessRights.ocppSettings ?? false) ||
      (curUser?.accessRights.ocppChargerAdmin ?? false),
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    tableSort: state.gui.tableSort[TABLE_NAME],
    virtualOcppChargers:
      (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.ocpp ?? [],
    siteId: state.cached.selectedSite,
    siteList: state.site.siteList,
  };
  return props;
};

interface IChargersState {
  isExpanded: boolean;
}

interface IChargersDispatch {
  onSort: (sort: IActionSortTable) => void;
  onEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargersDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_CHARGER, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  editHint: {
    id: "change settings",
  },
  lockedHint: {
    id: "charging is only allowed to valid RFID cards",
  },
  unlockedHint: {
    id: "charging is only allowed to everyone",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class ChargersComponent extends React.Component<
  IChargersProps & IChargersDispatch,
  IChargersState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IChargersState = {
    isExpanded: true,
  };

  public render() {
    const sortCol = (this.props.tableSort?.column ?? "chargepoint") as keyof IOcppCharger;
    const chargerIndicis = Array.from(Array(this.props.ocppChargers.length).keys()).sort((a, b) => {
      const keyA = this.props.ocppChargers[a]?.[sortCol!];
      const keyB = this.props.ocppChargers[b]?.[sortCol!];
      const sort = isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    const chargerItems = chargerIndicis.map((curIndex) => {
      return this.renderCharger(curIndex);
    });

    if (this.props.ocppChargers.length === 0) {
      return <></>;
    }

    return (
      <>
        <div id="ocpp-chargers">
          <h1
            className="link"
            onClick={() => {
              this.setState({ isExpanded: !this.state.isExpanded });
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.state.isExpanded ?
                <IconExpandMore />
              : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Chargers" />
          </h1>
          <Collapse in={this.state.isExpanded}>
            <div>
              <table className="datatable fullwidthwhite">
                <thead>
                  <tr>
                    {this.props.showId && <th className="optional-lg">Id</th>}
                    {this.makeSortHeader("chargepoint", <FormattedMessage id="Chargepoint-ID" />)}
                    {this.makeSortHeader("name", <FormattedMessage id="Name" />, ["optional-md"])}
                    {this.makeSortHeader("vendor", <FormattedMessage id="Vendor" />, [
                      "optional-md",
                    ])}
                    {this.makeSortHeader("model", <FormattedMessage id="Model" />, ["optional-lg"])}
                    {this.makeSortHeader("firmware", <FormattedMessage id="Software" />, [
                      "optional-lg",
                    ])}
                    {this.makeSortHeader("serial", <FormattedMessage id="Serial Number" />)}
                    {this.makeSortHeader(
                      "numberofconnectors",
                      <FormattedMessage id="Connectors" />,
                      ["optional-lg"],
                    )}
                    {this.makeSortHeader("restrictAuth", <FormattedMessage id="Authentication" />)}
                    <th></th>
                  </tr>
                </thead>
                <tbody>{chargerItems}</tbody>
              </table>
            </div>
          </Collapse>
        </div>
        <ModalChargerSettings />
      </>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IOcppCharger,
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort ?? { column: "chargepoint", isSortAscending: true },
      this.props.onSort,
      classes,
      title,
    );
  }

  private renderCharger(index: number) {
    const charger = this.props.ocppChargers[index];
    const virtualSiteName = getVirtualOcppChargerSourceName(
      this.props.virtualOcppChargers,
      charger.fk_charger,
      this.props.siteId,
      this.props.siteList,
    );
    const isVirtual = isSet(virtualSiteName);

    return (
      <tr key={`${charger.fk_charger}${charger.chargepoint}`}>
        {this.props.showId && <td className="optional-lg">{charger.fk_charger}</td>}
        <td>
          {charger.chargepoint}
          {virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(valueMessages.virtualSite, { site: virtualSiteName }),
            )}
        </td>
        <td className="optional-md">{charger.name}</td>
        <td className="optional-md">{charger.vendor}</td>
        <td className="optional-lg">{charger.model}</td>
        <td className="optional-lg">{charger.firmware}</td>
        <td className="nowrap">{charger.serial}</td>
        <td className="optional-lg">{charger.numberofconnectors}</td>
        <td className="nowrap">
          {charger.restrictAuth ?
            <>
              <IconLocked className="me-1" />
              <FormattedMessage id="Authorized" />
            </>
          : <>
              <IconUnlocked className="me-1" />
              <FormattedMessage id="Public" />
            </>
          }
        </td>
        {this.props.canModifyChargers && (
          <td className={`edit-col nowrap ${isVirtual ? "" : "img-link"}`}>
            {!isVirtual && (
              <span
                tabIndex={0}
                onKeyDown={tabClickHandler}
                onClick={() => this.props.onEdit(charger.fk_charger)}
                title={this.context.formatMessage(messages.editHint)}
              >
                <IconEdit />
              </span>
            )}
          </td>
        )}
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargersComponent);
